// src/pages/inventory/settings/CategoryManagement.tsx (updated)
import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Input, Switch, Space, Typography, Radio, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ShopOutlined, InboxOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { fetchProductCategories, createProductCategory, updateProductCategory, deleteProductCategory } from "../../../api/inventory/productCategoryApi";
import { ProductCategory } from "../../../types/inventory/ProductTypes";
import { extractValidationErrors } from "../../../utils/apiError";
import { StatusTag } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";
import { User } from "../../../types/admin/UserTypes";

const { Text } = Typography;
const { TextArea } = Input;

// Custom status rendering for category types
const renderCategoryType = (type: string) => {
	const icon = type === "commerce" ? <ShopOutlined /> : <InboxOutlined />;
	const color = type === "commerce" ? "blue" : "green";
	return (
		<Tag icon={icon} color={color}>
			{type === "commerce" ? "Commerce" : "Inventory"}
		</Tag>
	);
};

const renderScopeTag = (isGlobal: boolean) => (
	<Tag color={isGlobal ? "blue" : "default"}>
		{isGlobal ? "Global" : "Company"}
	</Tag>
);

interface CategoryManagementProps {
	user: User;
	companyId: number;
}

const CategoryManagement: React.FC<CategoryManagementProps> = ({ user, companyId }) => {
	const [categories, setCategories] = useState<ProductCategory[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [modalVisible, setModalVisible] = useState<boolean>(false);
	const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
	const [form] = Form.useForm();
	const { showError, showSuccess } = useNotifications();

	useEffect(() => {
		if (companyId) {
			loadCategories();
		}
	}, [companyId]);

	const loadCategories = async () => {
		setLoading(true);
		try {
			const { data } = await fetchProductCategories(1, 100, companyId);
			setCategories(data);
		} catch (error) {
			console.error("Error loading categories:", error);
			showError("Failed to load product categories");
		} finally {
			setLoading(false);
		}
	};

	const handleAdd = () => {
		setEditingCategory(null);
		form.resetFields();
		form.setFieldsValue({ is_active: true, category_type: "inventory" });
		setModalVisible(true);
	};

	const handleEdit = (category: ProductCategory) => {
		setEditingCategory(category);
		form.setFieldsValue(category);
		setModalVisible(true);
	};

	const handleDelete = (category: ProductCategory) => {
		Modal.confirm({
			title: "Delete Category",
			content: `Are you sure you want to delete "${category.name}"? This action cannot be undone.`,
			okText: "Delete",
			okType: "danger",
			onOk: async () => {
				try {
					await deleteProductCategory(category.id);
					showSuccess("Category deleted successfully");
					loadCategories();
				} catch (error) {
					showError("Failed to delete category");
				}
			},
		});
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			values.company_id = companyId;

			if (editingCategory) {
				await updateProductCategory(editingCategory.id, values);
				showSuccess("Category updated successfully");
			} else {
				await createProductCategory(values);
				showSuccess("Category created successfully");
			}

			setModalVisible(false);
			loadCategories();
		} catch (error) {
			// AntD form validation rejects with { errorFields } and already
			// renders inline field errors — nothing more to surface.
			if (error && typeof error === "object" && "errorFields" in error) {
				return;
			}
			// Surface the backend's 422 validation message(s) rather than a
			// generic failure (see src/utils/apiError.ts).
			showError(extractValidationErrors(error).join(" "));
		}
	};

	const columns: ColumnsType<ProductCategory> = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text: string) => <Text strong>{text}</Text>,
		},
		{
			title: "Type",
			dataIndex: "category_type",
			key: "category_type",
			width: 130,
			render: (type: string) => renderCategoryType(type),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			ellipsis: true,
			render: (text: string) => <Text type="secondary">{text || "—"}</Text>,
		},
		{
			title: "Status",
			dataIndex: "is_active",
			key: "is_active",
			width: 100,
			render: (isActive: boolean) => <StatusTag status={isActive ? "active" : "inactive"} />,
		},
		{
			title: "Scope",
			dataIndex: "is_global",
			key: "is_global",
			width: 100,
			render: (isGlobal: boolean) => renderScopeTag(isGlobal),
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			width: 120,
			render: (date: string) => (date ? new Date(date).toLocaleDateString() : "—"),
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			render: (_: any, record: ProductCategory) => (
				<Space size="small">
					<Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} disabled={record.is_global} />
					<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} disabled={record.is_global} />
				</Space>
			),
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Text type="secondary">Manage product categories for both commerce and inventory tracking</Text>
				<Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
					Add Category
				</Button>
			</div>

			<Table columns={columns} dataSource={categories} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} />

			<Modal title={editingCategory ? "Edit Category" : "Add Category"} open={modalVisible} onCancel={() => setModalVisible(false)} onOk={handleSubmit} width={600}>
				<Form form={form} layout="vertical">
					<Form.Item label="Category Name" name="name" rules={[{ required: true, message: "Please enter category name" }]}>
						<Input placeholder="e.g., Supplements, Lab Supplies" />
					</Form.Item>

					<Form.Item label="Category Type" name="category_type" rules={[{ required: true, message: "Please select category type" }]}>
						<Radio.Group>
							<Space direction="vertical">
								<Radio value="commerce">
									<Space>
										<ShopOutlined />
										<div>
											<div>
												<Text strong>Commerce</Text>
											</div>
											<Text type="secondary" style={{ fontSize: 12 }}>
												Products for sale (Shopify, online store)
											</Text>
										</div>
									</Space>
								</Radio>
								<Radio value="inventory">
									<Space>
										<InboxOutlined />
										<div>
											<div>
												<Text strong>Inventory</Text>
											</div>
											<Text type="secondary" style={{ fontSize: 12 }}>
												Internal stock tracking and management
											</Text>
										</div>
									</Space>
								</Radio>
							</Space>
						</Radio.Group>
					</Form.Item>

					<Form.Item label="Description" name="description">
						<TextArea rows={3} placeholder="Brief description of this category" />
					</Form.Item>

					<Form.Item label="Active" name="is_active" valuePropName="checked">
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default CategoryManagement;
